import { useEffect, useState } from "react"
import { Input } from "../../components/input"
import { Button } from "../../components/button"
import { Label } from "../../components/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/select"
import { normalizeUrl } from "@univdiam/ui/lib/UrlNormalize"

interface VariantRow {
  group: string;
  subGroup: string;
  subSubGroup: string;
  shape: string;
  size: string;
  stones: number;
  weight: string;
  availableSubGroups: Array<{ sub_group_id: string; name: string; status: string }>;
  availableSubSubGroups: Array<{ sub_sub_group_id: string; name: string; status: string }>;
}

interface DropdownOption {
  id: string;
  name: string;
  status: string;
}

interface ProductMasterFormProps {
  onSubmit: (data: any) => void;
  initialValues?: any;
  groupValues?: DropdownOption[];
  subGroupValues?: DropdownOption[];
  subSubGroupValues?: DropdownOption[];
  shapeSizeValues?: DropdownOption[];
  mainCategoryValues?: DropdownOption[];
  subCategoryValues?: DropdownOption[];
  collectionValues?: DropdownOption[];
  fetchSubGroups: (groupId: string) => Promise<DropdownOption[]>;
  fetchSubSubGroups: (subGroupId: string) => Promise<DropdownOption[]>;
  isEdit?: boolean;
}

const createNewRow = (): VariantRow => ({
  group: "",
  subGroup: "",
  subSubGroup: "",
  shape: "",
  size: "",
  stones: 0,
  weight: "",
  availableSubGroups: [],
  availableSubSubGroups: []
})

export function ProductMasterForm({
  onSubmit,
  initialValues = {},
  groupValues = [],
  subGroupValues = [],
  subSubGroupValues = [],
  shapeSizeValues = [],
  mainCategoryValues = [],
  subCategoryValues = [],
  collectionValues = [],
  fetchSubGroups,
  fetchSubSubGroups,
  isEdit = false,
}) {
  const [form, setForm] = useState({
    design: initialValues?.design || "",
    date: initialValues?.date || "",
    customer: initialValues?.customer || "",
    mainCategory: initialValues?.mainCategory || "",
    subCategory: initialValues?.subCategory || "",
    collection: initialValues?.collection || "",
    cadDoneBy: initialValues?.cadDoneBy || "",
    designedBy: initialValues?.designedBy || "",
    finding: initialValues?.finding || "",
    finish: initialValues?.finish || "",
    rhodium: initialValues?.rhodium || "",
    defaultImageTick: initialValues?.defaultImageTick || "",
    main_image: initialValues?.main_image || "",
    catalog_image: initialValues?.catalog_image || "",
    cad_image: initialValues?.cad_image || "",
    design_image: initialValues?.design_image || "",
    refrence_image: initialValues?.refrence_image || "",
    cad_file: initialValues?.cad_file || "",
    cad_stl: initialValues?.cad_stl || "",
  })

  const [main_image, setMainImage] = useState<File | null>(null)
  const [catalog_image, setCatalogImage] = useState<File | null>(null)
  const [cad_image, setCadImage] = useState<File | null>(null)
  const [design_image, setDesignImage] = useState<File | null>(null)
  const [refrence_image, setRefrenceImage] = useState<File | null>(null)
  const [cad_file, setCadFile] = useState<File | null>(null)
  const [cad_stl, setCadStl] = useState<File | null>(null)
  const [rows, setRows] = useState([createNewRow()])

  useEffect(() => {
    if (isEdit && Array.isArray(initialValues?.variants)) {
      const loadDependentData = async () => {
        const variantsWithData = await Promise.all(
          initialValues.variants.map(async (variant) => {
            // Fetch subgroups for this variant's group
            const availableSubGroups = variant.group
              ? await fetchSubGroups(variant.group)
              : [];

            // Fetch sub-subgroups for this variant's subgroup
            const availableSubSubGroups = variant.subGroup
              ? await fetchSubSubGroups(variant.subGroup)
              : [];

            return {
              ...variant,
              availableSubGroups,
              availableSubSubGroups
            };
          })
        );
        setRows(variantsWithData.length ? variantsWithData : [createNewRow()]);
      };

      loadDependentData();
    }
  }, [initialValues, isEdit, fetchSubGroups, fetchSubSubGroups]);


  // Update the row change handlers
  const handleGroupChange = async (index: number, groupId: string) => {
    const subGroups = await fetchSubGroups(groupId);

    setRows(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        group: groupId,
        subGroup: "", // Reset subgroup when group changes
        subSubGroup: "", // Reset sub-subgroup
        availableSubGroups: subGroups,
        availableSubSubGroups: [] // Clear sub-subgroups
      };
      return updated;
    });
  };

  const handleSubGroupChange = async (index: number, subGroupId: string) => {
    const subSubGroups = await fetchSubSubGroups(subGroupId);

    setRows(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        subGroup: subGroupId,
        subSubGroup: "", // Reset sub-subgroup
        availableSubSubGroups: subSubGroups
      };
      return updated;
    });
  };



  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setMainImage(file)
  }

  const handleCatalogFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setCatalogImage(file)
  }

  const handleCadImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setCadImage(file)
  }

  const handleDesignImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setDesignImage(file)
  }

  const handleRefrenceImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setRefrenceImage(file)
  }

  const handleCadFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setCadFile(file)
  }

  const handleCadStlFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setCadStl(file)
  }

  const handleRowChange = (index: number, field: keyof VariantRow, value: any) => {
    setRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddRow = () => setRows((prev) => [...prev, createNewRow()])
  const handleDeleteRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index))

  const handleSubmit = (e) => {
    e.preventDefault()

    const formData = new FormData()

    // append normal form data
    Object.entries(form).forEach(([key, value]) => {
      formData.append(key, value)
    })

    // append file
    if (main_image) {
      formData.append("main_image_file", main_image)
    }

    if (catalog_image) {
      formData.append("catalog_image_file", catalog_image)
    }

    if (cad_image) {
      formData.append("cad_image_file", cad_image)
    }

    if (design_image) {
      formData.append("design_image_file", design_image)
    }

    if (refrence_image) {
      formData.append("refrence_image_file", refrence_image)
    }

    if (cad_file) {
      formData.append("cad_file", cad_file)
    }

    if (cad_stl) {
      formData.append("cad_stl_file", cad_stl)
    }

    // append variants
    formData.append("variants", JSON.stringify(rows))

    onSubmit(formData) // your API will receive a FormData object
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-4">

      {/* --- FORM FIELDS --- */}
      {[
        ["design", "Design"],
        ["date", "Date", "date"],
        ["customer", "Customer"],
        // ["mainCategory", "Main Category"],
        // ["subCategory", "Sub Category"],
        // ["collection", "Collection"],
        ["cadDoneBy", "CAD Done By"],
        ["designedBy", "Designed By"],
        ["finding", "Finding"],
        ["finish", "Finish"],
        ["rhodium", "Rhodium"],
      ].map(([name, label, type = "text"]) => (
        <div key={name}>
          <Label htmlFor={name}>{label}</Label>
          <Input
            id={name}
            name={name}
            type={type}
            placeholder={`Enter ${label}`}
            value={form[name]}
            onChange={handleChange}
          />
        </div>
      ))}

      <div>
        <Label htmlFor="mainCategory">Main Category</Label>
        {mainCategoryValues?.length > 0 && (
          <Select
            value={form.mainCategory}
            onValueChange={(val) => setForm({ ...form, mainCategory: val })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Category" />
            </SelectTrigger>
            <SelectContent>
              {mainCategoryValues.map((g) => (
                <SelectItem key={g.name} value={g.name}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div>
        <Label htmlFor="subCategory">Sub Category</Label>
        {subCategoryValues?.length > 0 && (
          <Select
            value={form.subCategory}
            onValueChange={(val) => setForm({ ...form, subCategory: val })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Sub Category" />
            </SelectTrigger>
            <SelectContent>
              {subCategoryValues.map((g) => (
                <SelectItem key={g.name} value={g.name}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div>
        <Label htmlFor="collection">Collection</Label>
        {collectionValues?.length > 0 && (
          <Select
            value={form.collection}
            onValueChange={(val) => setForm({ ...form, collection: val })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Collection" />
            </SelectTrigger>
            <SelectContent>
              {collectionValues.map((g) => (
                <SelectItem key={g.name} value={g.name}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* --- DEFAULT IMAGE TYPE --- */}
      <div>
        <Label htmlFor="defaultImageTick">Default Image Type</Label>
        <Select
          value={form.defaultImageTick}
          onValueChange={(val) => setForm({ ...form, defaultImageTick: val })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select Image Type" />
          </SelectTrigger>
          <SelectContent>
            {[
              "Main",
              "Catalog",
              "Cad",
              "Design",
              "Reference",
              "Add New View",
              "CAD File 3DM",
              "CAD STL",
            ].map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isEdit && initialValues?.main_image && (
        <div className="mb-2">
          {initialValues.main_image.match(/\.(jpg|jpeg|png|gif)$/i) ? (
            <img
              src={normalizeUrl(initialValues.main_image)}
              alt="Existing File"
              className="rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(initialValues.main_image)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Download Existing File
            </a>
          )}
        </div>
      )}

      {/* --- FILE UPLOAD --- */}
      <div>
        <Label htmlFor="main_image">Main Image</Label>
        <Input
          id="main_image"
          name="main_image"
          type="file"
          accept=".jpg,.jpeg,.png,.pdf,.stl,.3dm,.heic,.heif"
          onChange={handleFileChange}
        />
      </div>

      {main_image && (
        <div className="mt-2">
          {main_image.type.startsWith("image/") ? (
            <img
              src={normalizeUrl(URL.createObjectURL(main_image))}
              alt="Preview"
              className="mt-2 rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(URL.createObjectURL(main_image))}
              download={main_image.name}
              className="text-blue-600 underline mt-2 inline-block"
            >
              Download {main_image.name}
            </a>
          )}
        </div>
      )}

      {/* catalog image  */}

      {isEdit && initialValues?.catalog_image && (
        <div className="mb-2">
          {initialValues.catalog_image.match(/\.(jpg|jpeg|png|gif)$/i) ? (
            <img
              src={normalizeUrl(initialValues.catalog_image)}
              alt="Existing File"
              className="rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(initialValues.catalog_image)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Download Existing File
            </a>
          )}
        </div>
      )}

      {/* --- FILE UPLOAD --- */}
      <div>
        <Label htmlFor="catalog_image">Catalog Image</Label>
        <Input
          id="catalog_image"
          name="catalog_image"
          type="file"
          accept=".jpg,.jpeg,.png,.pdf,.stl,.3dm,.heic,.heif"
          onChange={handleCatalogFileChange}
        />
      </div>

      {catalog_image && (
        <div className="mt-2">
          {catalog_image.type.startsWith("image/") ? (
            <img
              src={normalizeUrl(URL.createObjectURL(catalog_image))}
              alt="Preview"
              className="mt-2 rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(URL.createObjectURL(catalog_image))}
              download={catalog_image.name}
              className="text-blue-600 underline mt-2 inline-block"
            >
              Download {catalog_image.name}
            </a>
          )}
        </div>
      )}

      {/* catalog image end */}

      {/* cad image  */}
      {isEdit && initialValues?.cad_image && (
        <div className="mb-2">
          {initialValues.cad_image.match(/\.(jpg|jpeg|png|gif)$/i) ? (
            <img
              src={normalizeUrl(initialValues.cad_image)}
              alt="Existing File"
              className="rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(initialValues.cad_image)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Download Existing File
            </a>
          )}
        </div>
      )}

      {/* --- FILE UPLOAD --- */}
      <div>
        <Label htmlFor="cad_image">Cad Image</Label>
        <Input
          id="cad_image"
          name="cad_image"
          type="file"
          accept=".jpg,.jpeg,.png,.pdf,.stl,.3dm,.heic,.heif"
          onChange={handleCadImageFileChange}
        />
      </div>

      {cad_image && (
        <div className="mt-2">
          {cad_image.type.startsWith("image/") ? (
            <img
              src={normalizeUrl(URL.createObjectURL(cad_image))}
              alt="Preview"
              className="mt-2 rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(URL.createObjectURL(cad_image))}
              download={cad_image.name}
              className="text-blue-600 underline mt-2 inline-block"
            >
              Download {cad_image.name}
            </a>
          )}
        </div>
      )}
      {/* cad image end */}

      {/* design image  */}
      {isEdit && initialValues?.design_image && (
        <div className="mb-2">
          {initialValues.design_image.match(/\.(jpg|jpeg|png|gif)$/i) ? (
            <img
              src={normalizeUrl(initialValues.design_image)}
              alt="Existing File"
              className="rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(initialValues.design_image)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Download Existing File
            </a>
          )}
        </div>
      )}

      {/* --- FILE UPLOAD --- */}
      <div>
        <Label htmlFor="design_image">Design Image</Label>
        <Input
          id="design_image"
          name="design_image"
          type="file"
          accept=".jpg,.jpeg,.png,.pdf,.stl,.3dm,.heic,.heif"
          onChange={handleDesignImageFileChange}
        />
      </div>

      {design_image && (
        <div className="mt-2">
          {design_image.type.startsWith("image/") ? (
            <img
              src={normalizeUrl(URL.createObjectURL(design_image))}
              alt="Preview"
              className="mt-2 rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(URL.createObjectURL(design_image))}
              download={design_image.name}
              className="text-blue-600 underline mt-2 inline-block"
            >
              Download {design_image.name}
            </a>
          )}
        </div>
      )}
      {/* design image end */}

      {/* refrence image  */}
      {isEdit && initialValues?.refrence_image && (
        <div className="mb-2">
          {initialValues.refrence_image.match(/\.(jpg|jpeg|png|gif)$/i) ? (
            <img
              src={normalizeUrl(initialValues.refrence_image)}
              alt="Existing File"
              className="rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(initialValues.refrence_image)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Download Existing File
            </a>
          )}
        </div>
      )}

      {/* --- FILE UPLOAD --- */}
      <div>
        <Label htmlFor="refrence_image">Refrence Image</Label>
        <Input
          id="refrence_image"
          name="refrence_image"
          type="file"
          accept=".jpg,.jpeg,.png,.pdf,.stl,.3dm,.heic,.heif"
          onChange={handleRefrenceImageFileChange}
        />
      </div>

      {refrence_image && (
        <div className="mt-2">
          {refrence_image.type.startsWith("image/") ? (
            <img
              src={normalizeUrl(URL.createObjectURL(refrence_image))}
              alt="Preview"
              className="mt-2 rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(URL.createObjectURL(refrence_image))}
              download={refrence_image.name}
              className="text-blue-600 underline mt-2 inline-block"
            >
              Download {refrence_image.name}
            </a>
          )}
        </div>
      )}
      {/* refrence image end */}

      {/* cad file  */}
      {isEdit && initialValues?.cad_file && (
        <div className="mb-2">
          {initialValues.cad_file.match(/\.(jpg|jpeg|png|gif)$/i) ? (
            <img
              src={normalizeUrl(initialValues.cad_file)}
              alt="Existing File"
              className="rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(initialValues.cad_file)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Download Existing File
            </a>
          )}
        </div>
      )}

      {/* --- FILE UPLOAD --- */}
      <div>
        <Label htmlFor="cad_file">CAD File</Label>
        <Input
          id="cad_file"
          name="cad_file"
          type="file"
          accept=".jpg,.jpeg,.png,.pdf,.stl,.3dm,.heic,.heif"
          onChange={handleCadFileChange}
        />
      </div>

      {cad_file && (
        <div className="mt-2">
          {cad_file.type.startsWith("image/") ? (
            <img
              src={normalizeUrl(URL.createObjectURL(cad_file))}
              alt="Preview"
              className="mt-2 rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(URL.createObjectURL(cad_file))}
              download={cad_file.name}
              className="text-blue-600 underline mt-2 inline-block"
            >
              Download {cad_file.name}
            </a>
          )}
        </div>
      )}
      {/* cad file end */}

      {/* cad stl file  */}
      {isEdit && initialValues?.cad_stl && (
        <div className="mb-2">
          {initialValues.cad_stl.match(/\.(jpg|jpeg|png|gif)$/i) ? (
            <img
              src={normalizeUrl(initialValues.cad_stl)}
              alt="Existing File"
              className="rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(initialValues.cad_stl)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Download Existing File
            </a>
          )}
        </div>
      )}

      {/* --- FILE UPLOAD --- */}
      <div>
        <Label htmlFor="cad_stl">CAD STL</Label>
        <Input
          id="cad_stl"
          name="cad_stl"
          type="file"
          accept=".jpg,.jpeg,.png,.pdf,.stl,.3dm,.heic,.heif"
          onChange={handleCadStlFileChange}
        />
      </div>

      {cad_stl && (
        <div className="mt-2">
          {cad_stl.type.startsWith("image/") ? (
            <img
              src={normalizeUrl(URL.createObjectURL(cad_stl))}
              alt="Preview"
              className="mt-2 rounded border"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          ) : (
            <a
              href={normalizeUrl(URL.createObjectURL(cad_stl))}
              download={cad_stl.name}
              className="text-blue-600 underline mt-2 inline-block"
            >
              Download {cad_stl.name}
            </a>
          )}
        </div>
      )}
      {/* cad stl end */}

      {/* --- VARIANT TABLE --- */}
      <div className="border rounded-lg p-4 mt-6">
        <Label className="mb-2 block">Product Variants</Label>
        <table className="w-full border text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th>Group</th>
              <th>Sub Group</th>
              <th>Sub Sub Group</th>
              <th>Shape</th>
              <th>Size</th>
              <th>No. of Stones</th>
              <th>Total Weight</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>
                  <Select
                    value={row.group}
                    onValueChange={(val) => handleGroupChange(index, val)}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          (groupValues || []).find((g) => String(g.group_id) === String(row.group))
                            ?.name || "Select Group"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(groupValues || []).map((g) => (
                        <SelectItem key={g.group_id} value={String(g.group_id)}>
                          {g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td>
                  <Select
                    value={String(row.subGroup ?? "")}
                    onValueChange={(val) => handleSubGroupChange(index, val)}
                    disabled={!row.group || row.availableSubGroups.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          row.availableSubGroups.find(
                            (g) => String(g.sub_group_id) === String(row.subGroup)
                          )?.name || "Select Sub Group"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {row.availableSubGroups.map((g) => (
                        <SelectItem key={g.sub_group_id} value={String(g.sub_group_id)}>
                          {g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td>
                  <Select
                    value={String(row.subSubGroup ?? "")}
                    onValueChange={(val) => handleRowChange(index, "subSubGroup", val)}
                    disabled={!row.subGroup || row.availableSubSubGroups.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          row.availableSubSubGroups.find(
                            (g) => String(g.sub_sub_group_id) === String(row.subSubGroup)
                          )?.name || "Select Sub Sub Group"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {row.availableSubSubGroups.map((g) => (
                        <SelectItem key={g.sub_sub_group_id} value={String(g.sub_sub_group_id)}>
                          {g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td>
                  {/* <Input
                    value={row.shape}
                    onChange={(e) => handleRowChange(index, "shape", e.target.value)}
                  /> */}
                  {shapeSizeValues?.length > 0 && (
                    <Select
                      value={row.shape}
                      onValueChange={(val) => handleRowChange(index, "shape", val)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Sub Sub Group" />
                      </SelectTrigger>
                      <SelectContent>
                        {shapeSizeValues.map((g) => (
                          <SelectItem key={g.shape} value={g.shape}>
                            {g.shape}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </td>
                <td>
                  <Input
                    value={row.size}
                    onChange={(e) => handleRowChange(index, "size", e.target.value)}
                  />
                </td>
                <td>
                  <Input
                    type="number"
                    value={row.stones}
                    onChange={(e) => handleRowChange(index, "stones", Number(e.target.value))}
                  />
                </td>
                <td>
                  <Input
                    value={row.weight}
                    onChange={(e) => handleRowChange(index, "weight", e.target.value)}
                  />
                </td>
                <td>
                  <Button type="button" variant="destructive" onClick={() => handleDeleteRow(index)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4">
          <Button type="button" onClick={handleAddRow}>
            Add Row
          </Button>
        </div>
      </div>

      <Button type="submit">{isEdit ? "Update Product" : "Add Product"}</Button>
    </form>
  )
}
